/**
 * Service Principal Registry Frontend Types
 *
 * Feature: 023-service-principal-registry
 * Task: T014 - Frontend types
 * Constitution: v1.8.0 (Multi-Subscription SQL MI Edition)
 *
 * Re-exports shared types and adds frontend-specific types
 */

// Re-export all shared types using relative path for bundler compatibility
export * from '../../../../../shared/types/service-principal';

// Import types we need to extend
import type {
  ServicePrincipal,
  SpWorkspaceAssignment,
  SpReconciliationLog,
  ReconciliationStatus,
  ReconciliationType,
} from '../../../../../shared/types/service-principal';

// ============================================================================
// Frontend-specific pagination types
// ============================================================================

export interface PaginationParams {
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  [key: string]: string | number | boolean | undefined;
}

// ============================================================================
// Frontend-specific filter types
// ============================================================================

export interface ServicePrincipalFilters extends PaginationParams {
  status?: string;
  category?: string;
  search?: string;
}

export interface AssignmentFilters extends PaginationParams {
  driftStatus?: string;
  expected?: boolean;
}

export interface ReconciliationLogFilters extends PaginationParams {
  reconciliationType?: string;
  status?: string;
  startDate?: string;
  endDate?: string;
  limit?: number;
}

export interface DriftFilters extends PaginationParams {
  driftStatus?: string;
  servicePrincipalId?: string;
}

// ============================================================================
// Paginated response wrapper (matches backend PaginatedResponse)
// ============================================================================

export interface PaginatedMeta {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  meta: PaginatedMeta;
}

// ============================================================================
// Response type aliases for API layer
// ============================================================================

export type ServicePrincipalListResponse = PaginatedResponse<ServicePrincipal>;
export type SpWorkspaceAssignmentListResponse = PaginatedResponse<SpWorkspaceAssignment>;
export type SpReconciliationLogListResponse = PaginatedResponse<SpReconciliationLog>;

// ============================================================================
// Dashboard summary for status page
// ============================================================================

export interface SpDashboardSummary {
  totalServicePrincipals: number;
  activeServicePrincipals: number;
  inactiveServicePrincipals: number;
  totalAssignments: number;
  driftsDetected: number;
  activeDrifts: number;
  driftsByStatus: Record<string, number>;
  lastReconciliation: SpReconciliationLog | null;
  nextScheduledRun: {
    type: ReconciliationType;
    scheduledAt: Date;
  } | null;
}

// ============================================================================
// Alias for backward compatibility
// ============================================================================

/**
 * @deprecated Use CreateAssignmentRequest from shared types
 */
export type CreateSpWorkspaceAssignmentRequest = {
  workspaceId: string;
  permissionLevel: string;
  expected?: boolean;
};

/**
 * @deprecated Use SpReconciliationLog from shared types
 */
export type ReconciliationRun = SpReconciliationLog;

// ============================================================================
// Form validation rules
// ============================================================================

export const VALIDATION_RULES = {
  // Pattern constants
  GUID_PATTERN: /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i,
  KEY_VAULT_URI_PATTERN: /^https:\/\/[a-z0-9-]+\.vault\.azure\.net\/secrets\/[a-zA-Z0-9-]+/,

  // Length constants
  DISPLAY_NAME_MIN_LENGTH: 3,
  DISPLAY_NAME_MAX_LENGTH: 256,
  PURPOSE_MAX_LENGTH: 1000,

  // Field rules
  applicationId: {
    required: true,
    pattern: /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i,
    message: 'Must be a valid GUID (e.g., 12345678-1234-1234-1234-123456789abc)',
  },
  displayName: {
    required: true,
    minLength: 3,
    maxLength: 256,
    message: 'Display name must be between 3 and 256 characters',
  },
  purpose: {
    required: false,
    maxLength: 1000,
    message: 'Purpose must be less than 1000 characters',
  },
  keyVaultSecretUri: {
    required: false,
    pattern: /^https:\/\/[a-z0-9-]+\.vault\.azure\.net\/secrets\/[a-zA-Z0-9-]+/,
    message: 'Must be a valid Key Vault secret URI',
  },
} as const;

// ============================================================================
// Extended SpReconciliationLog with auto-fixed count (for backwards compat)
// ============================================================================

/**
 * Extended reconciliation log with frontend-specific fields
 */
export interface SpReconciliationLogExtended extends Omit<SpReconciliationLog, 'driftsFixed'> {
  driftsFixed: number;
  driftsAutoFixed: number;
}

// ============================================================================
// Reconciliation status response
// ============================================================================

export interface ReconciliationStatusResponse {
  isRunning: boolean;
  currentRun: SpReconciliationLog | null;
  lastRun: SpReconciliationLog | null;
  scheduledRuns: {
    type: ReconciliationType;
    cronExpression: string;
    nextRun: Date;
  }[];
}
